import { HttpClient } from '@angular/common/http';
import { TranslateLoader } from '@ngx-translate/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

type TranslationObject = Record<string, unknown>;

export class CustomTranslateLoader implements TranslateLoader {
    constructor(
        private http: HttpClient,
        private prefix = './assets/i18n/',
        private suffix = '.json',
        private additionalFiles: string[] = ['favorites']
    ) { }

    /**
     * Gets the translations from the server
     */
    public getTranslation(lang: string): Observable<TranslationObject> {
        // Main translation file
        const mainFile = this.http.get<TranslationObject>(`${this.prefix}${lang}${this.suffix}`);

        // Language-specific additions are merged last so they can patch missing or stale values.
        const additionsFile = this.http.get<TranslationObject>(`${this.prefix}${lang}-additions${this.suffix}`).pipe(
            catchError(() => of({} as TranslationObject))
        );

        // Additional translation files
        const additionalRequests = this.additionalFiles.map(file =>
            this.http.get<TranslationObject>(`${this.prefix}${file}-${lang}${this.suffix}`).pipe(
                catchError(() => of({} as TranslationObject)) // If file doesn't exist, return empty object
            )
        );

        // Combine all files
        return forkJoin([mainFile, ...additionalRequests, additionsFile]).pipe(
            map(responses => {
                const [main, ...additionals] = responses as TranslationObject[];
                // Merge all translation objects
                const merged = additionals.reduce<TranslationObject>((result, current) => this.mergeDeep(result, current), main);
                // Enrich with alias keys so variants like snake_case and lowercase dotted resolve
                return this.addKeyAliases(merged);
            })
        );
    }

    /**
     * Deep merge two objects
     */
    private mergeDeep(target: TranslationObject, source: TranslationObject): TranslationObject {
        if (!source) return target;

        const output: TranslationObject = { ...target };

        if (this.isObject(target) && this.isObject(source)) {
            for (const key of Object.keys(source)) {
                const sVal = source[key];
                const tVal = (target as TranslationObject)[key];
                if (this.isObject(sVal)) {
                    if (!this.isObject(tVal)) {
                        output[key] = sVal;
                    } else {
                        output[key] = this.mergeDeep(tVal, sVal);
                    }
                } else {
                    output[key] = sVal;
                }
            }
        }

        return output;
    }

    /**
     * Check if value is an object
     */
    private isObject(item: unknown): item is Record<string, unknown> {
        return !!item && typeof item === 'object' && !Array.isArray(item);
    }

    /**
     * Generate alias keys for all leaf translations to support alternate lookup styles:
     * - snake_case lowercase: common_connectivity_online
     * - lowercase dotted path: common.connectivity.online
     * - UPPERCASE_SNAKE for convenience: COMMON_CONNECTIVITY_ONLINE
     * This avoids showing raw keys when some parts of the app/library use different casing.
     */
    private addKeyAliases(translations: TranslationObject): TranslationObject {
        if (!this.isObject(translations)) return translations;

        const flatEntries: { path: string[]; value: unknown }[] = [];

        const walk = (node: unknown, path: string[]) => {
            if (!this.isObject(node)) {
                flatEntries.push({ path, value: node });
                return;
            }
            for (const k of Object.keys(node)) {
                walk((node as Record<string, unknown>)[k], [...path, k]);
            }
        };

        walk(translations, []);

        const out: TranslationObject = { ...translations };

        const setDeep = (obj: TranslationObject, path: string[], value: unknown) => {
            let curr: TranslationObject = obj;
            for (let i = 0; i < path.length - 1; i++) {
                const key = path[i];
                const next = curr[key];
                if (!this.isObject(next)) curr[key] = {} as TranslationObject;
                curr = (curr[key] as TranslationObject);
            }
            curr[path[path.length - 1]] = value;
        };

        for (const entry of flatEntries) {
            const val = entry.value;
            // Only alias primitive/string-like leaves
            if (val === null || this.isObject(val)) continue;

            const segments = entry.path;
            if (!segments.length) continue;

            const lowerSegments = segments.map(s => String(s).toLowerCase());
            const upperSegments = segments.map(s => String(s).toUpperCase());

            const snakeLower = lowerSegments.join('_');
            const snakeUpper = upperSegments.join('_');
            const dotLower = lowerSegments.join('.');

            // 1) Flat snake_case lower alias at root
            if (typeof out[snakeLower] === 'undefined') {
                out[snakeLower] = val;
            }

            // 2) Flat UPPER_SNAKE alias at root
            if (typeof out[snakeUpper] === 'undefined') {
                out[snakeUpper] = val;
            }

            // 3) Lowercase dotted nested tree under a distinct root to avoid clashing with existing UPPER roots
            const firstLower = lowerSegments[0];
            if (typeof out[firstLower] === 'undefined' || this.isObject(out[firstLower])) {
                setDeep(out, lowerSegments, val);
            }

            // 4) Also provide a direct lower dotted key at root if someone uses translation service with non-nested store
            if (typeof out[dotLower] === 'undefined') {
                out[dotLower] = val;
            }
        }

        return out;
    }
}
